'use client'

import dynamic from 'next/dynamic'
import { retryImport } from '@/lib/retryImport'
import { ACTIVE_LOCALES, DEFAULT_LOCALE, type Locale } from '@/lib/seo'

// Sofortauftrag 17.07.2026 — wrapt ausschließlich den [locale]-Routenbaum
// (/en/*, /tr/*) mit einer eigenen, bereits korrekt-sprachigen i18next-
// Instanz. useTranslation() in bestehenden PageClient.tsx-Komponenten löst
// react-i18next-Kontext-basiert auf — innerhalb dieses Baums also automatisch
// gegen DIESE Instanz, nicht gegen das globale DE-Singleton aus lib/i18n.ts.
// Bestehende, unpräfigierte Seiten sind davon vollständig unberührt.
//
// Pruefauftrag 29.07.2026 (Blocker 1, CWV-Root-Cause der 4 praefigierten
// Sprachbaeume): Live-Fund -- die vorherige Version bekam "messages" als
// Server->Client-Prop von (public-intl)/[locale]/layout.tsx uebergeben.
// Next.js muss JEDE Server->Client-Prop im RSC-Flight-Payload serialisieren
// und INLINE in JEDES HTML-Dokument einbetten (kein externer, cachebarer
// Chunk moeglich) -- gemessen 518 KB reiner JSON-Text pro einzelnem
// Seitenaufruf (die komplette common.json-Datei, nicht nur die auf der
// jeweiligen Seite benutzten Schluessel), NEU heruntergeladen und geparst
// bei JEDEM Request, nie vom Browser gecacht. Das erklaert den gemessenen
// LCP-Render-Delay von 1,3-2,6s (Browser muss das riesige Inline-JSON parsen,
// bevor React den Baum committen/malen kann) -- TTFB und JS-Ausfuehrungszeit
// selbst waren bereits unauffaellig, das Problem war reine HTML-Dokumentgroesse.
//
// Diese Architektur (SERVERSEITIGER dynamischer Import + Prop-Uebergabe)
// stammte bewusst aus der 50-Sprachen-Aera (siehe lib/i18nLocale.ts
// Kopfkommentar: "10-15MB JSON in JEDEN Client-Bundle backen" waere bei 50
// statischen Imports die Alternative gewesen). Seit dem Sprachprojekt-
// Abschluss 24.07.2026 (nur noch 4 praefigierte aktive Sprachen: en/tr/fr/ar)
// gilt diese Abwaegung nicht mehr.
//
// Verfeinerung (nach Live-Messung mit hoher Laufzeitvarianz bei einem ersten,
// gemeinsamen statischen Import aller 4 Dateien in EINER Datei -- das haette
// einem /tr/*-Besucher weiterhin alle 4 Sprachdateien zusammen aufgebuerdet,
// ~2,6 MB statt nur der eigenen ~600 KB): next/dynamic() pro Sprache mit
// EIGENER Quelldatei (localeProviders/*Provider.tsx) -- Next.js/Webpack
// erzeugt dadurch 4 UNABHAENGIGE, separat ladbare JS-Chunks. Ein Besucher
// von /en/* laedt NUR den EnProvider-Chunk, nicht zusaetzlich tr/fr/ar.
// ssr:true (Default) sorgt dafuer, dass Next.js den Import waehrend des
// Server-Renders AUFLOEST, bevor die HTML-Antwort verschickt wird -- der
// korrekte, uebersetzte Text steht weiterhin von Anfang an im SSR-HTML
// (kein Lade-/Flackerzustand fuer Crawler oder Nutzer).
//
// Root-Cause-Fix 25.08.2026 (kritischer Live-Bug): der Sprachinstallations-
// Auftrag 24.08.2026 hat 21 weitere Locales in locale-config.js von
// "frozen" nach "active" verschoben (ACTIVE_LOCALES/generateStaticParams
// erzeugen seitdem echte, oeffentlich erreichbare Seiten dafuer), diese
// Datei aber NICHT mitgezogen -- PROVIDERS kannte weiterhin nur die
// urspruenglichen 8 Nicht-DE-Sprachen. Fuer jede der 21 neuen Sprachen gab
// `PROVIDERS[locale]` `undefined` zurueck, der Fallback unten reichte
// `children` OHNE eigene i18next-Instanz durch -- useTranslation() loeste
// dadurch gegen das globale, hart auf 'de' fixierte Singleton aus
// PublicProviders.tsx auf. Ergebnis: /fi, /hu, /az, ... lieferten HTTP 200
// mit vollstaendig deutschem Seiteninhalt, obwohl die jeweilige
// src/locales/<code>/common.json bereits korrekt uebersetzt war (siehe
// scripts/locale-providers-check.js -- verhindert, dass diese Liste
// kuenftig erneut unbemerkt von ACTIVE_LOCALES abweicht).
//
// PROVIDERS bleibt bewusst eine explizite, statische Map (kein Loop ueber
// ACTIVE_LOCALES mit dynamischem Importpfad): next/dynamic() + Next.js/
// Webpack brauchen einen statisch analysierbaren import()-Pfad pro Sprache,
// um daraus einen EIGENEN, separat ladbaren JS-Chunk zu erzeugen (siehe
// Kommentar oben). Die einzige Quelle der Wahrheit, WELCHE Sprachen ueberhaupt
// einen Provider brauchen, bleibt ACTIVE_LOCALES aus locale-config.js -- die
// Vollstaendigkeit dieser Map dagegen wird durch
// scripts/locale-providers-check.js (npm run locale-providers:check)
// mechanisch gegen ACTIVE_LOCALES geprueft, nicht durch Handarbeit.
const PROVIDERS: Record<string, React.ComponentType<{ children: React.ReactNode }>> = {
  en: dynamic(() => retryImport(() => import('./localeProviders/EnProvider'))),
  tr: dynamic(() => retryImport(() => import('./localeProviders/TrProvider'))),
  fr: dynamic(() => retryImport(() => import('./localeProviders/FrProvider'))),
  ar: dynamic(() => retryImport(() => import('./localeProviders/ArProvider'))),
  es: dynamic(() => retryImport(() => import('./localeProviders/EsProvider'))),
  pt: dynamic(() => retryImport(() => import('./localeProviders/PtProvider'))),
  it: dynamic(() => retryImport(() => import('./localeProviders/ItProvider'))),
  af: dynamic(() => retryImport(() => import('./localeProviders/AfProvider'))),
  az: dynamic(() => retryImport(() => import('./localeProviders/AzProvider'))),
  bn: dynamic(() => retryImport(() => import('./localeProviders/BnProvider'))),
  cs: dynamic(() => retryImport(() => import('./localeProviders/CsProvider'))),
  da: dynamic(() => retryImport(() => import('./localeProviders/DaProvider'))),
  fa: dynamic(() => retryImport(() => import('./localeProviders/FaProvider'))),
  fi: dynamic(() => retryImport(() => import('./localeProviders/FiProvider'))),
  fil: dynamic(() => retryImport(() => import('./localeProviders/FilProvider'))),
  ga: dynamic(() => retryImport(() => import('./localeProviders/GaProvider'))),
  gn: dynamic(() => retryImport(() => import('./localeProviders/GnProvider'))),
  he: dynamic(() => retryImport(() => import('./localeProviders/HeProvider'))),
  hi: dynamic(() => retryImport(() => import('./localeProviders/HiProvider'))),
  hr: dynamic(() => retryImport(() => import('./localeProviders/HrProvider'))),
  hu: dynamic(() => retryImport(() => import('./localeProviders/HuProvider'))),
  id: dynamic(() => retryImport(() => import('./localeProviders/IdProvider'))),
  ja: dynamic(() => retryImport(() => import('./localeProviders/JaProvider'))),
  kk: dynamic(() => retryImport(() => import('./localeProviders/KkProvider'))),
  ko: dynamic(() => retryImport(() => import('./localeProviders/KoProvider'))),
  ku: dynamic(() => retryImport(() => import('./localeProviders/KuProvider'))),
  lb: dynamic(() => retryImport(() => import('./localeProviders/LbProvider'))),
  ms: dynamic(() => retryImport(() => import('./localeProviders/MsProvider'))),
  ne: dynamic(() => retryImport(() => import('./localeProviders/NeProvider'))),
  // Sprachinstallations-Auftrag 2 (28.09.2026): die letzten 21 Sprachen.
  nl: dynamic(() => retryImport(() => import('./localeProviders/NlProvider'))),
  ro: dynamic(() => retryImport(() => import('./localeProviders/RoProvider'))),
  ru: dynamic(() => retryImport(() => import('./localeProviders/RuProvider'))),
  uk: dynamic(() => retryImport(() => import('./localeProviders/UkProvider'))),
  pl: dynamic(() => retryImport(() => import('./localeProviders/PlProvider'))),
  ur: dynamic(() => retryImport(() => import('./localeProviders/UrProvider'))),
  vi: dynamic(() => retryImport(() => import('./localeProviders/ViProvider'))),
  zh: dynamic(() => retryImport(() => import('./localeProviders/ZhProvider'))),
  yue: dynamic(() => retryImport(() => import('./localeProviders/YueProvider'))),
  ta: dynamic(() => retryImport(() => import('./localeProviders/TaProvider'))),
  sw: dynamic(() => retryImport(() => import('./localeProviders/SwProvider'))),
  no: dynamic(() => retryImport(() => import('./localeProviders/NoProvider'))),
  sv: dynamic(() => retryImport(() => import('./localeProviders/SvProvider'))),
  sk: dynamic(() => retryImport(() => import('./localeProviders/SkProvider'))),
  sl: dynamic(() => retryImport(() => import('./localeProviders/SlProvider'))),
  sq: dynamic(() => retryImport(() => import('./localeProviders/SqProvider'))),
  th: dynamic(() => retryImport(() => import('./localeProviders/ThProvider'))),
  zu: dynamic(() => retryImport(() => import('./localeProviders/ZuProvider'))),
  xh: dynamic(() => retryImport(() => import('./localeProviders/XhProvider'))),
  sn: dynamic(() => retryImport(() => import('./localeProviders/SnProvider'))),
  nd: dynamic(() => retryImport(() => import('./localeProviders/NdProvider'))),
}

export function LocaleProviders({
  locale,
  children,
}: {
  locale: Locale
  children: React.ReactNode
}) {
  const Provider = PROVIDERS[locale]
  if (!Provider) {
    // Root-Cause-Fix 25.08.2026: fuer eine ECHTE ACTIVE_LOCALE (ausser der
    // unpraefigierten Default-Sprache 'de', die diesen Baum nie durchlaeuft,
    // siehe layout.tsx-Guard) darf dieser Zweig nie erreicht werden -- er
    // wuerde sonst wieder den stillen, unbemerkbaren Fallback auf das
    // globale DE-Singleton reproduzieren, der den obigen Live-Bug verursacht
    // hat. Statt still durchzureichen: laut scheitern (von der ErrorBoundary
    // in PublicProviders.tsx aufgefangen), damit eine kuenftig vergessene
    // Provider-Datei sofort sichtbar wird statt wochenlang unbemerkt falschen
    // Content auszuliefern. scripts/locale-providers-check.js faengt diesen
    // Fall zusaetzlich schon vor dem Build ab.
    if (locale !== DEFAULT_LOCALE && (ACTIVE_LOCALES as readonly string[]).includes(locale)) {
      throw new Error(
        `LocaleProviders: keine Provider-Zuordnung fuer aktive Locale "${locale}" -- ` +
          `PROVIDERS in LocaleProviders.tsx muss um einen Eintrag ergaenzt werden ` +
          `(siehe scripts/locale-providers-check.js).`
      )
    }
    return <>{children}</>
  }
  return <Provider>{children}</Provider>
}
